import React, { useState, useEffect } from 'react';
import { estRubrique, libelleRubrique } from '../services/jdRubrique';

// La date de démarrage saisie sur le besoin (« 2026-12-01 », ISO ou non),
// écrite comme on la lit en Suisse romande. Le salaire max n'apparaît
// jamais ici : la fiche de poste part chez des candidats et des partenaires.
const dateDemarrageCH = (v) => { const m = String(v || '').slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[3]}.${m[2]}.${m[1]}` : null; };

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const toLines = (v) => (Array.isArray(v) ? v : String(v || '').split(/\r?\n/)).map(s => String(s).trim()).filter(Boolean);
const splitSkills = (v) => Array.isArray(v) ? v.filter(Boolean) : String(v || '').split(/[,;/•\n]+/).map(s => s.trim()).filter(Boolean);

// ── Page publique de fiche de poste : erpia.ch/jd/:token ─────────────────────
// Consomme GET /public/jd/:token → { besoin, jd, pdf_url } — SANS auth. Le back
// masque le client sur les liens anonymes ; on affiche ce qui est fourni.
export default function PublicJDPage({ token }) {
  const [data, setData] = useState(undefined); // undefined = chargement, null = invalide/révoqué
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/public/jd/${encodeURIComponent(token)}`);
        if (!r.ok) { if (!cancelled) setData(null); return; }
        const j = await r.json().catch(() => ({}));
        if (!cancelled) setData(j.data || j);
      } catch { if (!cancelled) setData(null); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const b = data?.besoin || {};
  const jd = data?.jd || {};
  const missions = toLines(jd.missions);
  const profil = toLines(jd.profil);
  const skills = splitSkills(b.competences || b.skills);
  const meta = [
    ['Contrat', b.contrat || b.type_contrat || 'Mission'],
    ['Séniorité', b.seniorite || b.niveau || '—'],
    [b.tjm || b.tarif ? 'Tarif' : 'Salaire', b.tjm || b.tarif || b.salaire || '—'],
    ['Démarrage', dateDemarrageCH(b.date_demarrage) || b.demarrage || 'Dès que possible'],
    ...((b.localisation || b.lieu) ? [['Lieu', b.localisation || b.lieu]] : []),
  ];
  const sectTitle = { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#E2231A', margin: '26px 0 12px' };

  return (
    <div data-theme="light" style={{ minHeight: '100vh', background: '#f2f3f5', fontFamily: "'IBM Plex Sans', system-ui, sans-serif", color: '#1a1c24', padding: '28px 16px 60px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* En-tête erpia */}
      <div style={{ width: '100%', maxWidth: '780px', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <span style={{ fontFamily: "'Archivo',sans-serif", fontSize: '21px', fontWeight: 800, letterSpacing: '-0.03em', color: '#0b0c11' }}>erpia<span style={{ color: '#E2231A' }}>.</span></span>
        <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#E2231A' }}>Fiche de poste</span>
        <div style={{ flex: 1 }} />
        {data?.pdf_url && (
          // URL ABSOLUE OBLIGATOIRE. Cette page est servie par l'application ;
          // le PDF vient de l'API. Un « /public/jd/… » nu se résolvait sur le
          // domaine de l'application, qui ne connaît pas cette adresse et
          // affichait son écran de connexion — à un candidat qui n'a pas de
          // compte (vécu 18.09). Le serveur renvoie désormais l'URL complète ;
          // ce préfixe couvre les liens déjà partagés, dont la page peut
          // encore recevoir l'ancienne forme.
          <a href={data.pdf_url.startsWith('http') ? data.pdf_url : `${BASE}${data.pdf_url}`} target="_blank" rel="noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', borderRadius: '8px', background: '#16181f', color: '#fff', fontSize: '13px', fontWeight: 600, textDecoration: 'none' }}>
            <BI icon="file-earmark-pdf" /> Télécharger le PDF
          </a>
        )}
      </div>

      {data === undefined && (
        <div style={{ width: '100%', maxWidth: '780px', background: '#fff', border: '1px solid #e5e7ec', borderRadius: '16px', padding: '60px 24px', textAlign: 'center', color: '#9aa0ac', fontSize: '13px' }}>
          Chargement de la fiche…
        </div>
      )}
      {data === null && (
        <div style={{ width: '100%', maxWidth: '780px', background: '#fff', border: '1px solid #e5e7ec', borderRadius: '16px', padding: '60px 24px', textAlign: 'center' }}>
          <BI icon="link-45deg" style={{ fontSize: '26px', color: '#9aa0ac' }} />
          <div style={{ marginTop: '10px', fontFamily: "'Archivo',sans-serif", fontSize: '18px', fontWeight: 800, color: '#0b0c11' }}>Lien invalide ou révoqué</div>
          <div style={{ marginTop: '6px', fontSize: '13px', color: '#5f656f' }}>Ce lien de fiche de poste n'est plus actif — demandez un nouveau lien à votre contact.</div>
        </div>
      )}

      {data && data !== null && data !== undefined && (
        <div style={{ width: '100%', maxWidth: '780px', background: '#fff', border: '1px solid #e5e7ec', borderRadius: '3px 16px 16px 16px', padding: 'clamp(28px,4vw,52px)', position: 'relative' }}>
          <div style={{ position: 'absolute', top: '-1px', left: '-1px', height: '20px', padding: '0 14px', background: '#fff', borderTop: '1px solid #e5e7ec', borderRight: '1px solid #e5e7ec', borderRadius: '3px 12px 0 0', transform: 'translateY(-100%)' }} />

          <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: 'clamp(24px,3vw,32px)', fontWeight: 800, letterSpacing: '-0.02em', color: '#0b0c11', lineHeight: 1.1 }}>{b.titre || 'Poste'}<span style={{ color: '#E2231A' }}>.</span></div>
          {(b.client || b.ville) && <div style={{ marginTop: '5px', fontSize: '13.5px', color: '#5f656f' }}>{[b.client, b.ville].filter(Boolean).join(' · ')}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: '1px', background: '#edeff2', border: '1px solid #edeff2', borderRadius: '10px', overflow: 'hidden', marginTop: '20px' }}>
            {meta.map(([k, v], i) => (
              <div key={i} style={{ background: '#fff', padding: '11px 13px' }}>
                <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#878d99' }}>{k}</div>
                <div style={{ marginTop: '3px', fontSize: '13px', fontWeight: 600, color: '#0b0c11', fontFamily: "'IBM Plex Mono',monospace" }}>{v}</div>
              </div>
            ))}
          </div>

          {jd.contexte && (<>
            <div style={sectTitle}>Contexte</div>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.7, color: '#1a1c24', maxWidth: '64ch' }}>{jd.contexte}</p>
          </>)}

          {missions.length > 0 && (<>
            <div style={sectTitle}>Missions principales</div>
            <div>
              {missions.map((m, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '13px', padding: '9px 0', borderTop: i === 0 ? 'none' : '1px solid #edeff2' }}>
                  <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px', fontWeight: 600, color: '#9aa0ac', flex: 'none', paddingTop: '2px' }}>{String(i + 1).padStart(2, '0')}</span>
                  <span style={{ fontSize: '14px', lineHeight: 1.55 }}>{m}</span>
                </div>
              ))}
            </div>
          </>)}

          {skills.length > 0 && (<>
            <div style={sectTitle}>Compétences requises</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {skills.map((s, i) => <span key={i} style={{ fontSize: '12px', fontWeight: 500, color: '#4f5663', background: '#f1f2f5', padding: '4px 10px', borderRadius: '6px' }}>{s}</span>)}
            </div>
          </>)}

          {profil.length > 0 && (<>
            <div style={sectTitle}>Profil recherché</div>
            <div>
              {profil.map((l, i) => estRubrique(l) ? (
                <div key={i} style={{ fontSize: '13px', fontWeight: 600, padding: i ? '12px 0 2px' : '2px 0 2px' }}>{libelleRubrique(l)}</div>
              ) : (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '5px 0' }}>
                  <span style={{ width: '5px', height: '5px', background: '#E2231A', marginTop: '8px', flex: 'none' }} />
                  <span style={{ fontSize: '14px', lineHeight: 1.5 }}>{l}</span>
                </div>
              ))}
            </div>
          </>)}

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '32px', paddingTop: '16px', borderTop: '1px solid #edeff2', fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', color: '#9aa0ac' }}>
            <span>Document généré par erpia</span>
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ display: 'inline-flex', width: '11px', height: '11px' }}><svg viewBox="0 0 32 32" style={{ width: '100%', height: '100%' }}><rect width="32" height="32" rx="6" fill="#E2231A" /><path d="M13.5 6.5H18.5V13.5H25.5V18.5H18.5V25.5H13.5V18.5H6.5V13.5H13.5Z" fill="#fff" /></svg></span>
              erpia
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
